import { LoadingButton } from "@mui/lab";
import {
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { ActionItemsContext } from "contexts/ActionItems";
import { OrganizationUserContext } from "contexts/Organization";
import { Dispatch, SetStateAction, useContext } from "react";
import { CustomEvent } from "schemas/functions";

import DiscoverBanner from "features/Influencer/Discover/Results/Banner";

import ActionItemList from "../../ActionItemList";
import CustomDomainSection from "../../CustomDomainSection";
import OnboardingStepper from "../../OnboardingStepper";
import { styles } from "../styles";

interface IProps {
  fetchLoading: boolean;
  loadingChat: boolean;
  loading: boolean;
  initializeConversation: (input: string) => void;
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
}

const DEFAULT_BUTTONS = [
  "✉️ Send emails",
  "📥 Manage Inbox",
  "✨ Personalized suggestions",
  "💬 Customer Support",
];

export default function DefaultView({
  fetchLoading,
  loadingChat,
  loading,
  initializeConversation,
  input,
  setInput,
}: IProps) {
  const { actionItems } = useContext(ActionItemsContext);
  const { profile } = useContext(OrganizationUserContext);

  const today = new Date();

  const getTodayDate = () => {
    return today.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Grid
      sx={{
        height: "calc(100vh - 120px)",
        px: 3,
      }}
      container
      justifyContent="center"
      alignItems="center"
    >
      {loadingChat && (
        <Backdrop
          sx={{
            zIndex: 9999,
          }}
          open={loadingChat}
        >
          <CircularProgress />
        </Backdrop>
      )}
      <Grid item xs={12} container sx={{ pb: 10 }}>
        <Grid item xs={12}>
          <Typography
            variant="h3"
            sx={{ textAlign: "center", mt: 10 }}
            gutterBottom
          >
            What can I help you with today?
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent="center"
            gap={1}
            sx={{ mb: 2 }}
          >
            {DEFAULT_BUTTONS.map((buttonText: string) => {
              return (
                <LoadingButton
                  key={buttonText}
                  disabled={loading}
                  size="small"
                  variant="outlined"
                  sx={styles.button}
                  onClick={() => initializeConversation(buttonText)}
                >
                  {buttonText}
                </LoadingButton>
              );
            })}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box
            component="form"
            onSubmit={(e: CustomEvent) => {
              e.preventDefault();
              initializeConversation(e.target.value);
            }}
            display="flex"
            alignItems={"center"}
            gap={1}
          >
            <TextField
              value={input}
              onChange={(e) => setInput(e.target.value)}
              fullWidth
              placeholder={"What kind of brands would you like to pitch today"}
              disabled={loading}
              multiline
              minRows={1}
              maxRows={10}
              onKeyDown={(e: CustomEvent) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  initializeConversation(e.target.value);
                }
              }}
              sx={styles.textField}
            />
            <IconButton
              type="submit"
              disabled={loading || !input.trim()}
              sx={{ width: 30, height: 30 }}
            >
              <Box component="span" className="fa-solid fa-paper-plane-top" />
            </IconButton>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ mt: 3 }}>
            <DiscoverBanner />
          </Box>
        </Grid>
        {(!profile?.hasOnboarded || actionItems.length > 0) && (
          <>
            <Grid item xs={12}>
              <Box display="flex">
                <Typography paragraph variant="h6">
                  <Box
                    component="i"
                    className={"fa-solid fa-rocket"}
                    sx={{ pr: 1, color: "secondary.main" }}
                  />{" "}
                  Action Items for {getTodayDate()}
                </Typography>
              </Box>
            </Grid>
            {!profile?.hasOnboarded && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <OnboardingStepper />
                </Box>
              </Grid>
            )}
            {profile?.hasOnboarded && actionItems.length > 0 && (
              <>
                <Grid item xs={12}>
                  <ActionItemList fetchLoading={fetchLoading} />
                  <CustomDomainSection />
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
}
